import React from 'react';

export const DiagnosticStatus = {
  LOADING: "loading",
  ONLINE: "online",
  DEGRADED: "degraded",
  OFFLINE: "offline",
  UNKNOWN: "unknown"
};


function parseIcons(response) {
  const fallbackIcons = {
    loading: { icon: "fa fa-spinner fa-spin", color: "#1E90FF", label: "Checking..." },
    online: { icon: "fa fa-circle", color: "#008000", label: "Online" },
    degraded: { icon: "fa fa-exclamation-triangle", color: "#FFA500", label: "Degraded" },
    offline: { icon: "fa fa-circle", color: "#808080", label: "Offline" },
    unknown: { icon: "fa fa-question-circle", color: "#C0C0C0", label: "Unknown" },
    action: { icon: "fas fa-sync", color: "#1E90FF", label: "Run" }
  };

  try {
    if (response && response.icons) {
      const parsedIcons = JSON.parse(response.icons);
      return { ...fallbackIcons, ...parsedIcons };
    }
  } catch (err) {
    console.error("Failed to parse icons:", err);
  }

  return fallbackIcons;
}

export function mapStatus(response) {
  if (!response) {
    return DiagnosticStatus.UNKNOWN;
  }
  if (response.loading) {
    return DiagnosticStatus.LOADING;
  }

  if (response.state) {
    switch (response.state.toLowerCase()) {
      case 'online':
        return DiagnosticStatus.ONLINE;
      case 'offline':
        return DiagnosticStatus.OFFLINE;
      case 'degraded':
        return DiagnosticStatus.DEGRADED;
      default:
        return DiagnosticStatus.UNKNOWN;
    }
  }

  const online = response.online;
  const details = response.details?.["CALIX-SMX"];

  if (!details) {
    return online ? DiagnosticStatus.ONLINE : DiagnosticStatus.UNKNOWN;
  }

  const { device_connected, service_assigned, ont_is_missing } = details;

  if (ont_is_missing || device_connected === false) {
    return DiagnosticStatus.OFFLINE;
  }
  if (online && device_connected) {
    return DiagnosticStatus.ONLINE;
  }
  if (device_connected && !service_assigned) {
    return DiagnosticStatus.DEGRADED;
  }

  return DiagnosticStatus.UNKNOWN;
}

function renderLogsContent(logs) {
  if (!logs) return null;

  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return path.split('.').reduce((current, key) => current?.[key], obj) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const Badge = ({ children, variant = "secondary", style = {} }) => (
    <span style={{
      display: "inline-block",
      padding: "4px 8px",
      fontSize: "11px",
      fontWeight: "600",
      borderRadius: "12px",
      backgroundColor: variant === "success" ? "#d4edda" :
                     variant === "danger" ? "#f8d7da" :
                     variant === "warning" ? "#fff3cd" :
                     variant === "info" ? "#d1ecf1" : "#e2e3e5",
      color: variant === "success" ? "#155724" :
             variant === "danger" ? "#721c24" :
             variant === "warning" ? "#856404" :
             variant === "info" ? "#0c5460" : "#383d41",
      border: `1px solid ${variant === "success" ? "#c3e6cb" : 
                           variant === "danger" ? "#f5c6cb" : 
                           variant === "warning" ? "#ffeaa7" : 
                           variant === "info" ? "#bee5eb" : "#d6d8db"}`,
      marginRight: "6px",
      marginBottom: "4px",
      ...style
    }}>
      {children}
    </span>
  );

  const DataRow = ({ label, value, variant }) => (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: "8px",
      gap: "8px"
    }}>
      <span style={{
        fontWeight: "600",
        color: "#495057",
        minWidth: "120px",
        fontSize: "12px"
      }}>
        {label}:
      </span>
      <Badge variant={variant}>{value}</Badge>
    </div>
  );

  const getBooleanBadge = (value, trueVariant = "success", falseVariant = "danger") => {
    if (value === true) return <Badge variant={trueVariant}>Yes</Badge>;
    if (value === false) return <Badge variant={falseVariant}>No</Badge>;
    return <Badge variant="secondary">Unknown</Badge>;
  };

  return (
    <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
      {safeGet(logs, 'online') !== null && (
        <div style={{ marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid #dee2e6" }}>
          <DataRow
            label="Overall Status"
            value={safeGet(logs, 'online') ? "Online" : "Offline"}
            variant={safeGet(logs, 'online') ? "success" : "danger"}
          />
        </div>
      )}

      {(safeGet(logs, 'device_assigned') !== null || safeGet(logs, 'ont_is_missing') !== null) && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{
            fontWeight: "600",
            color: "#495057",
            marginBottom: "12px",
            fontSize: "13px",
            borderBottom: "1px solid #dee2e6",
            paddingBottom: "6px"
          }}>
            Device Status
          </div>

          {safeGet(logs, 'device_assigned') !== null && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
              <span style={{ fontWeight: "600", color: "#495057", minWidth: "120px", fontSize: "12px" }}>
                Device Assigned:
              </span>
              {getBooleanBadge(safeGet(logs, 'device_assigned'))}
            </div>
          )}

          {safeGet(logs, 'ont_is_missing') !== null && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
              <span style={{ fontWeight: "600", color: "#495057", minWidth: "120px", fontSize: "12px" }}>
                ONT Missing:
              </span>
              {getBooleanBadge(safeGet(logs, 'ont_is_missing'), "danger", "success")}
            </div>
          )}
        </div>
      )}

      {safeGet(logs, 'details.CALIX-SMX') && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{
            fontWeight: "600",
            color: "#495057",
            marginBottom: "12px",
            fontSize: "13px",
            borderBottom: "1px solid #dee2e6",
            paddingBottom: "6px"
          }}>
            CALIX-SMX Interface Details
          </div>

          {safeGet(logs, 'details.CALIX-SMX.device_connected') !== null && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
              <span style={{ fontWeight: "600", color: "#495057", minWidth: "120px", fontSize: "12px" }}>
                Device Connected:
              </span>
              {getBooleanBadge(safeGet(logs, 'details.CALIX-SMX.device_connected'))}
            </div>
          )}

          {safeGet(logs, 'details.CALIX-SMX.service_assigned') !== null && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
              <span style={{ fontWeight: "600", color: "#495057", minWidth: "120px", fontSize: "12px" }}>
                Service Assigned:
              </span>
              {getBooleanBadge(safeGet(logs, 'details.CALIX-SMX.service_assigned'))}
            </div>
          )}

          {safeGet(logs, 'details.CALIX-SMX.device_assigned') !== null && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
              <span style={{ fontWeight: "600", color: "#495057", minWidth: "120px", fontSize: "12px" }}>
                Device Assigned:
              </span>
              {getBooleanBadge(safeGet(logs, 'details.CALIX-SMX.device_assigned'))}
            </div>
          )}

          {safeGet(logs, 'details.CALIX-SMX.ont_is_missing') !== null && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
              <span style={{ fontWeight: "600", color: "#495057", minWidth: "120px", fontSize: "12px" }}>
                ONT Missing:
              </span>
              {getBooleanBadge(safeGet(logs, 'details.CALIX-SMX.ont_is_missing'), "danger", "success")}
            </div>
          )}
        </div>
      )}

      {safeGet(logs, 'details.CALIX-SMX.detailed_payloads.global_ont_response') && Array.isArray(safeGet(logs, 'details.CALIX-SMX.detailed_payloads.global_ont_response')) && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{
            fontWeight: "600",
            color: "#495057",
            marginBottom: "12px",
            fontSize: "13px",
            borderBottom: "1px solid #dee2e6",
            paddingBottom: "6px"
          }}>
            ONT Device Information
          </div>

          {safeGet(logs, 'details.CALIX-SMX.detailed_payloads.global_ont_response', []).map((ont, index) => (
            <div key={index} style={{
              backgroundColor: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "8px"
            }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                {safeGet(ont, 'model') && <Badge variant="info">{safeGet(ont, 'model')}</Badge>}
                {safeGet(ont, 'serial-number') && <Badge variant="secondary">{safeGet(ont, 'serial-number')}</Badge>}
                {safeGet(ont, 'ont-state') && (
                  <Badge variant={safeGet(ont, 'ont-state') === "ONT Pushed" ? "success" : "warning"}>
                    {safeGet(ont, 'ont-state')}
                  </Badge>
                )}
              </div>

              {safeGet(ont, 'subscriber-name') && (
                <div style={{ marginTop: "8px", fontSize: "11px", color: "#6c757d" }}>
                  <strong>Subscriber:</strong> {safeGet(ont, 'subscriber-name')}
                </div>
              )}

              {safeGet(ont, 'vendor-id') && (
                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                  <strong>Vendor:</strong> {safeGet(ont, 'vendor-id')}
                </div>
              )}

              {safeGet(ont, 'device-name') && (
                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                  <strong>Device:</strong> {safeGet(ont, 'device-name')}
                </div>
              )}

              {safeGet(ont, 'date') && (
                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                  <strong>Last Updated:</strong> {(() => {
                    try {
                      return new Date(safeGet(ont, 'date')).toLocaleString();
                    } catch {
                      return safeGet(ont, 'date');
                    }
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {Array.isArray(logs) && logs.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{
            fontWeight: "600",
            color: "#495057",
            marginBottom: "12px",
            fontSize: "13px",
            borderBottom: "1px solid #dee2e6",
            paddingBottom: "6px"
          }}>
            Device Information
          </div>

          {logs.map((device, index) => (
            <div key={index} style={{
              backgroundColor: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "8px"
            }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                {safeGet(device, 'model') && <Badge variant="info">{safeGet(device, 'model')}</Badge>}
                {safeGet(device, 'serial-number') && <Badge variant="secondary">{safeGet(device, 'serial-number')}</Badge>}
                {safeGet(device, 'ont-state') && (
                  <Badge variant={safeGet(device, 'ont-state') === "ONT Pushed" ? "success" : "warning"}>
                    {safeGet(device, 'ont-state')}
                  </Badge>
                )}
                {safeGet(device, 'ont-type') && <Badge variant="info">{safeGet(device, 'ont-type')}</Badge>}
              </div>

              {safeGet(device, 'subscriber-name') && (
                <div style={{ marginTop: "8px", fontSize: "11px", color: "#6c757d" }}>
                  <strong>Subscriber:</strong> {safeGet(device, 'subscriber-name')}
                </div>
              )}

              {safeGet(device, 'device-name') && (
                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                  <strong>Device:</strong> {safeGet(device, 'device-name')}
                </div>
              )}

              {safeGet(device, 'shutdown') !== null && (
                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                  <strong>Shutdown:</strong> {safeGet(device, 'shutdown') ? 'Yes' : 'No'}
                </div>
              )}

              {safeGet(device, 'battery-present') && (
                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                  <strong>Battery Present:</strong> {safeGet(device, 'battery-present')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <details style={{ marginTop: "16px" }}>
        <summary style={{
          cursor: "pointer",
          fontWeight: "600",
          color: "#6c757d",
          fontSize: "11px",
          padding: "8px 0"
        }}>
          Show Raw Data (Debug)
        </summary>
        <pre style={{
          fontSize: "10px",
          margin: "8px 0 0 0",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "Monaco, Consolas, 'Courier New', monospace",
          backgroundColor: "#fff",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          padding: "8px",
          maxHeight: "200px",
          overflow: "auto"
        }}>
          {JSON.stringify(logs, null, 2)}
        </pre>
      </details>
    </div>
  );
}


export default function DiagnosticIndicator({
  diagnosticResponse,
  statusDiagnostic,
  isLoading,
  onRunAction,
  onToggleLogs,
  style,
  device,
  status,
  logs,
  hasLogs,
  hideLogs = false
}) {

  const icons = parseIcons(statusDiagnostic || (Array.isArray(diagnosticResponse) ? diagnosticResponse[0] : diagnosticResponse));

  const actions = Array.isArray(diagnosticResponse)
    ? diagnosticResponse.map(diag => diag.operationName).filter(Boolean)
    : (diagnosticResponse?.operationName ? [diagnosticResponse.operationName] : []);

  const handleActionClick = (actionKey) => {
    if (onRunAction) {
      const specificDiagnostic = Array.isArray(diagnosticResponse)
        ? diagnosticResponse.find(diag => diag.operationName === actionKey)
        : diagnosticResponse;
      onRunAction(actionKey, device, specificDiagnostic);
    }
  };

  const currentIcon = icons[status];
  const actionIcon = icons.action;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "6px 12px",
        backgroundColor: "#f8f9fa",
        border: `2px solid #C0C0C0`,
        borderRadius: "8px",
        fontSize: "12px",
        fontWeight: "500",
        minWidth: 180,
        maxWidth: 300,
        ...style
      }}
    >
      {currentIcon && (
        <div style={{
          fontSize: "16px",
          marginRight: "8px",
          color: currentIcon.color,
          display: "flex",
          alignItems: "center"
        }}>
          <i className={currentIcon.icon} aria-hidden="true"></i>
        </div>
      )}

      <div style={{
        flex: 1,
        color: currentIcon?.color || "#666",
        fontWeight: "600"
      }}>
        {currentIcon?.label || status}
      </div>

      {actions.length > 0 && (
        <div style={{ display: "flex", gap: "4px", marginLeft: "8px" }}>
          {actions.map((action) => {
            const actionDiagnostic = Array.isArray(diagnosticResponse)
              ? diagnosticResponse.find(diag => diag.operationName === action)
              : diagnosticResponse;
            const actionIcons = parseIcons(actionDiagnostic);
            const currentActionIcon = actionIcons.action;

            return (
              <button
                key={action}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleActionClick(action);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 8px",
                  fontSize: "11px",
                  fontWeight: "500",
                  border: "none",
                  borderRadius: 4,
                  background: "transparent",
                  cursor: "pointer",
                  whiteSpace: "nowrap"
                }}
                title={`Run ${action}`}
              >
                <i
                  className={currentActionIcon?.icon || actionIcon?.icon}
                  style={{
                    color: currentActionIcon?.color || actionIcon?.color || "#1E90FF",
                    fontSize: "14px"
                  }}
                  aria-hidden="true"
                ></i>
              </button>
            );
          })}
        </div>
      )}

      {hasLogs && !hideLogs && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleLogs && onToggleLogs();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "4px 8px",
            fontSize: "14px",
            fontWeight: "500",
            border: "none",
            borderRadius: 4,
            background: "transparent",
            cursor: "pointer",
            whiteSpace: "nowrap"
          }}
        >
          <i className="fa fa-bars"></i>
        </button>
      )}

    </div>
  );
}
